import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ChartLine,
  ChatCircle,
  CreditCard,
  Database,
  Envelope,
  FacebookLogo,
  Folder,
  GoogleLogo,
  Lightning,
  Sparkle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Icon } from '../../../../../../components/Icon';
import { RowValue, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { Banner } from '../../../../../../components/ui/banner';
import { Button } from '../../../../../../components/ui/button';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { useToast } from '../../../../../../components/ui/toast';
import { flagsQuery, integrationsQuery, type IntegrationStatus } from '../../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/integrations')({
  component: IntegrationsPage,
});

interface Item {
  id: string;
  icon: PhosphorIcon;
  name: string;
  detail: string;
}

const CLIENT_FACING: Item[] = [
  { id: 'meta', icon: FacebookLogo, name: 'Meta · Facebook & Instagram', detail: 'Posting and account access' },
  {
    id: 'google',
    icon: GoogleLogo,
    name: 'Google · Calendar, Meet & Business Profile',
    detail: 'Meetings and listings',
  },
  { id: 'razorpay', icon: CreditCard, name: 'Razorpay', detail: 'Payments' },
];

const BEHIND_THE_SCENES: Item[] = [
  { id: 'stream', icon: ChatCircle, name: 'Stream · chat', detail: 'Client and team chat' },
  { id: 'resend', icon: Envelope, name: 'Resend · email', detail: 'Transactional email' },
  { id: 'n8n', icon: Sparkle, name: 'n8n · business audits', detail: 'Audit automation' },
  { id: 'r2', icon: Folder, name: 'Cloudflare R2 · files', detail: 'Uploads and downloads' },
  { id: 'redis', icon: Database, name: 'Upstash Redis · rate limits', detail: 'Sign-in limits and short-lived locks' },
  { id: 'qstash', icon: Lightning, name: 'Upstash QStash · background jobs', detail: 'Audits and scheduled work' },
  { id: 'posthog', icon: ChartLine, name: 'PostHog · product analytics', detail: 'Product events' },
];

const TAG: Record<IntegrationStatus, { label: string; tone: 'green' | 'blue' | 'red' | 'grey' }> = {
  healthy: { label: 'Healthy', tone: 'green' },
  configured: { label: 'Set up', tone: 'blue' },
  error: { label: 'Needs attention', tone: 'red' },
  unconfigured: { label: 'Not set up', tone: 'grey' },
  unknown: { label: 'Not checked yet', tone: 'grey' },
};

/** Health only, never secrets: the server says whether each service is set up and, where it can, whether it answers. */
function IntegrationsPage() {
  const toast = useToast();
  const health = useQuery({ ...integrationsQuery, retry: false });
  const flags = useQuery(flagsQuery);
  const stripeOn = flags.data?.find((f) => f.key === 'stripe_checkout')?.enabled ?? false;

  const byId = new Map((health.data ?? []).map((h) => [h.id, h]));
  const trouble = [...CLIENT_FACING, ...BEHIND_THE_SCENES].find((i) => byId.get(i.id)?.status === 'error');

  const renderItem = (item: Item) => {
    const status = byId.get(item.id)?.status ?? 'unknown';
    const tag = TAG[status];
    return (
      <SettingRow
        key={item.id}
        icon={item.icon}
        title={item.name}
        subtitle={item.detail}
        trailing={<StatusTag label={tag.label} tone={tag.tone} dot />}
      />
    );
  };

  return (
    <>
      {health.isError ? <Banner tone="error">Couldn’t check your integrations right now.</Banner> : null}
      {trouble ? (
        <Banner
          tone="warning"
          title={`${trouble.name.split(' · ')[0]} needs attention`}
          action={
            // TODO(api): there is no log viewer yet.
            <Button variant="ghost" size="small" onClick={() => toast({ title: 'Logs are coming soon', tone: 'info' })}>
              View logs
            </Button>
          }
        >
          {byId.get(trouble.id)?.detail ?? 'Something stopped working. We retry automatically.'}
        </Banner>
      ) : null}

      <SettingCard title="Client-facing" subtitle="Services clients notice right away if they stop working.">
        {CLIENT_FACING.map(renderItem)}
        <Link
          to="/subdomain/admin/settings/workspace/flags"
          className="flex items-center gap-(--mbm-space-4) rounded-(--mbm-radius-sm) py-(--mbm-space-4) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <span className="flex w-(--mbm-size-icon-tile) shrink-0 justify-center text-text-secondary">
            <Icon icon={CreditCard} size="md" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-label-large text-text-primary">Stripe</span>
            <span className="text-body-small text-text-secondary">
              {stripeOn ? 'On in Feature flags' : 'Turned off in Feature flags'}
            </span>
          </span>
          <RowValue>{stripeOn ? 'On' : 'Off'}</RowValue>
        </Link>
      </SettingCard>

      <SettingCard
        title="Behind the scenes"
        subtitle="Keys and secrets live on the server. Change them there, not here."
      >
        {BEHIND_THE_SCENES.map(renderItem)}
      </SettingCard>
    </>
  );
}
